import { useState } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { DarkModeToggle } from '../UI/DarkModeToggle';
import { useTheme } from '../../contexts/ThemeContext';
import { AuditLogsModal } from '../Audit/AuditLogsModal';

interface AppBarProps {
  drawerWidth: number;
  handleDrawerToggle: () => void;
}

export const AppBar = ({
  drawerWidth,
  handleDrawerToggle
}: AppBarProps) => {
  const { user, logout, isHotelManager } = useAuth();
  const { darkMode } = useTheme();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [auditModalOpen, setAuditModalOpen] = useState(false);

  const isZonesPage = location.pathname === '/zones';

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleAddZone = () => {
    window.dispatchEvent(new CustomEvent('openCreateZoneDialog'));
  };

  return (
    <MuiAppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        backgroundColor: darkMode ? '#1e1e2d' : '#fff',
        borderBottom: darkMode
          ? '1px solid rgba(255, 255, 255, 0.06)'
          : '1px solid #f0f0f0',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 } }}>
        {/* Mobile menu button */}
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            mr: 1.5,
            display: { sm: 'none' },
            color: darkMode ? '#e8eaed' : '#666'
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Left side actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {/* Zone Ekle button - only on Zones page and for Hotel Managers */}
          {isZonesPage && isHotelManager && (
            <Button
              startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
              onClick={handleAddZone}
              sx={{
                borderRadius: '10px',
                px: 2, py: 0.6,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.8rem',
                backgroundColor: '#4294e3',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(66, 148, 227, 0.25)',
                '&:hover': {
                  backgroundColor: '#3680c9',
                  boxShadow: '0 4px 12px rgba(66, 148, 227, 0.35)',
                }
              }}
            >
              Zone Ekle
            </Button>
          )}
        </Box>

        {/* Right side actions */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Audit Logs */}
            <Tooltip title="Sistem Logları" arrow>
              <IconButton
                onClick={() => setAuditModalOpen(true)}
                sx={{
                  color: darkMode ? '#9aa0a6' : '#999',
                  width: 36, height: 36,
                  '&:hover': {
                    color: darkMode ? '#e8eaed' : '#4294e3',
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(66, 148, 227, 0.08)'
                  }
                }}
              >
                <AssessmentRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Dark Mode Toggle */}
            <DarkModeToggle />

            {/* User Avatar + Menu */}
            <Tooltip title={user.username} arrow>
              <IconButton
                onClick={handleMenu}
                sx={{
                  ml: 0.5, p: 0.5,
                  '&:hover': { backgroundColor: 'transparent' }
                }}
              >
                <Avatar
                  sx={{
                    width: 34, height: 34,
                    bgcolor: darkMode ? 'rgba(66, 148, 227, 0.15)' : 'rgba(66, 148, 227, 0.1)',
                    color: '#4294e3',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    border: darkMode ? '2px solid rgba(66, 148, 227, 0.3)' : '2px solid rgba(66, 148, 227, 0.15)',
                  }}
                >
                  {user.username.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #f0f0f0',
                  mt: 0.5,
                  minWidth: 160,
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e' }}>
                  {user.username}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#999' }}>
                  {isHotelManager ? 'Hotel Manager' : 'Hotel Staff'}
                </Typography>
              </Box>
              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1.2, px: 2,
                  fontSize: '0.85rem',
                  color: '#c62828',
                  gap: 1,
                  '&:hover': { backgroundColor: 'rgba(198, 40, 40, 0.06)' }
                }}
              >
                <LogoutRoundedIcon sx={{ fontSize: 18 }} />
                Çıkış Yap
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>

      {/* Audit Logs Modal */}
      <AuditLogsModal
        open={auditModalOpen}
        onClose={() => setAuditModalOpen(false)}
      />
    </MuiAppBar>
  );
};
